import { AnimatePresence, motion } from 'motion/react'
import { usePostHog } from 'posthog-js/react'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import { useConfig } from '~/domains/configuration/queries/config'
import type { ConfigName } from '~/domains/configuration/types'
import { AddFilterButton } from './AddFilterButton'
import { filterBar, filterBarWrapper } from './FilterBar.css'
import { FilterChip } from './FilterChip'
import { SavedSearchChips } from './SavedSearchChips'
import { SaveFilterButton } from './SaveFilterButton'
import type { FilterDefinition, FiltersState } from './types'
import {
  definitionKey,
  filtersToQueryString,
  getActiveDefinitionsInKeyOrder,
  normalizeQueryString,
  queryStringToFilters,
  sanitizeFilters
} from './utils'

const chipMotion = {
  initial: { opacity: 0, scale: 0.85, x: -6 },
  animate: { opacity: 1, scale: 1, x: 0 },
  exit: { opacity: 0, scale: 0.85, x: -6 }
}

export type FilterBarProps = {
  definitions: FilterDefinition[]
  filters: FiltersState
  onChange: (filters: FiltersState, areDefault?: boolean) => void
  savedSearchesKey?: ConfigName
  defaultFilters?: FiltersState
  /** Called once the saved default search or `defaultFilters` have been applied (or found unnecessary). */
  onDefaultsResolved?: () => void
  analyticsKey?: string
}

export const FilterBar = ({
  definitions,
  filters,
  onChange,
  savedSearchesKey,
  defaultFilters,
  onDefaultsResolved,
  analyticsKey
}: FilterBarProps): ReactElement => {
  const posthog = usePostHog()
  const [pendingDef, setPendingDef] = useState<FilterDefinition | null>(null)
  const defaultsAppliedRef = useRef(false)

  const { data: savedSearches = [], isLoading } = useConfig(
    savedSearchesKey as 'search.saved_orders',
    { enabled: !!savedSearchesKey }
  )
  const { mutate: updateConfig } = useUpdateConfig(savedSearchesKey)

  useEffect(() => {
    if (!savedSearchesKey || isLoading) return
    if (Object.keys(filters).length > 0 || defaultsAppliedRef.current) {
      onDefaultsResolved?.()
      return
    }
    defaultsAppliedRef.current = true

    const defaultSearch = savedSearches.find((s) => s.isDefault)
    if (defaultSearch) {
      onChange(queryStringToFilters(defaultSearch.queryString), true)
    } else if (defaultFilters) {
      onChange(defaultFilters, true)
    } else {
      onDefaultsResolved?.()
    }
  }, [
    savedSearchesKey,
    isLoading,
    filters,
    savedSearches,
    defaultFilters,
    onChange,
    onDefaultsResolved
  ])

  const pendingKey = pendingDef ? definitionKey(pendingDef) : null
  const activeDefinitions = getActiveDefinitionsInKeyOrder(
    filters,
    definitions
  ).filter((def) => definitionKey(def) !== pendingKey)

  const trackFilter = (
    action:
      | 'add'
      | 'change'
      | 'remove'
      | 'save'
      | 'select_saved'
      | 'set_default',
    properties: Record<string, unknown> = {}
  ) => {
    if (!analyticsKey) return
    posthog.capture('filters_interaction', {
      analyticsKey,
      action,
      ...properties
    })
  }

  const commitFilters = (newFilters: FiltersState) => {
    onChange(sanitizeFilters(newFilters))
  }

  const handleRemoveFilter = (def: FilterDefinition) => {
    trackFilter('remove', { filterKey: definitionKey(def) })
    const newFilters = { ...filters }
    if (def.type === 'dateRange') {
      delete newFilters[def.keyAfter]
      delete newFilters[def.keyBefore]
    } else {
      delete newFilters[def.key]
    }
    commitFilters(newFilters)
  }

  const currentQs = filtersToQueryString(filters)
  const hasFilters = activeDefinitions.length > 0 || pendingDef !== null
  const isAlreadySaved = savedSearches.some(
    (s) => normalizeQueryString(s.queryString) === currentQs
  )
  const isDefaultFilters =
    defaultFilters !== undefined &&
    currentQs === filtersToQueryString(defaultFilters)

  const handleSave = (name: string) => {
    const normalizedCurrentQs = normalizeQueryString(currentQs)
    trackFilter('save')
    updateConfig({
      value: [...savedSearches, { name, queryString: normalizedCurrentQs }]
    })
  }

  const handleDeleteSearch = (index: number) => {
    updateConfig({
      value: savedSearches.filter((_, i) => i !== index)
    })
  }

  const handleSetDefaultSearch = (index: number) => {
    const currentSearch = savedSearches[index]
    trackFilter('set_default', {
      isDefaultAfterClick: currentSearch?.isDefault !== true
    })
    updateConfig({
      value: savedSearches.map((s, i) => ({
        ...s,
        isDefault: i === index ? !s.isDefault : false
      }))
    })
  }

  const handleSelectSearch = (queryString: string) => {
    trackFilter('select_saved')
    onChange(queryStringToFilters(queryString))
  }

  return (
    <div className={filterBarWrapper}>
      <SavedSearchChips
        searches={savedSearches}
        activeQueryString={currentQs}
        onSelect={handleSelectSearch}
        onDelete={handleDeleteSearch}
        onSetDefault={handleSetDefaultSearch}
      />
      <div className={filterBar}>
        <AnimatePresence initial={false} mode="popLayout">
          {activeDefinitions.map((def) => (
            <motion.div key={definitionKey(def)} {...chipMotion}>
              <FilterChip
                definition={def}
                filters={filters}
                onChange={(newFilters) => {
                  trackFilter('change', { filterKey: definitionKey(def) })
                  commitFilters(newFilters)
                }}
                onRemove={() => handleRemoveFilter(def)}
              />
            </motion.div>
          ))}
          {pendingDef && (
            <motion.div
              key={`pending-${definitionKey(pendingDef)}`}
              {...chipMotion}
            >
              <FilterChip
                definition={pendingDef}
                filters={filters}
                onChange={(newFilters) => {
                  commitFilters(newFilters)
                  if (pendingDef.type !== 'dateRange') {
                    setPendingDef(null)
                  }
                }}
                onRemove={() => setPendingDef(null)}
                onClose={() => setPendingDef(null)}
                initiallyOpen
              />
            </motion.div>
          )}
        </AnimatePresence>
        <span title="Ajouter un filtre">
          <AddFilterButton
            definitions={definitions}
            filters={filters}
            onSelect={(definition) => {
              trackFilter('add', { filterKey: definitionKey(definition) })
              setPendingDef(definition)
            }}
          />
        </span>
        <AnimatePresence>
          {savedSearchesKey &&
            hasFilters &&
            !isAlreadySaved &&
            !isDefaultFilters && (
              <motion.div {...chipMotion}>
                <SaveFilterButton onSave={handleSave} />
              </motion.div>
            )}
        </AnimatePresence>
      </div>
    </div>
  )
}
